import { css } from "@emotion/css";
import {
  useObservable,
  useObservableState,
  useSubscription,
} from "observable-hooks";
import React, { FC, memo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDrag } from "react-use-gesture";
import { FullGestureState } from "react-use-gesture/dist/types";
import { filter } from "rxjs/operators";
import { InnerNote } from "../InnerNote";
import {
  DoneAnimatingEvent,
  drag$,
  DragEndEvent,
  hasNoDestination$,
} from "./Control";
import { DraggedNote } from "./DraggedNote";
import { destinations$ } from "./Placeholder";

const Portaled = ({ children }) => {
  return ReactDOM.createPortal(
    children,
    document.getElementById("dragging-root")!
  );
};

export const DraggableNote: FC<{
  id: string;
  controlledHeight?: boolean;
}> = memo(({ id, controlledHeight = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const startingPosition = useRef<DOMRect>(null);
  const [dragging, setDragging] = useState(false);
  const [animating, setAnimating] = useState(false);

  const hasNoDestination = useObservableState(hasNoDestination$, true);

  const animationEnd$ = useObservable(
    () =>
      drag$.pipe(
        filter((e): e is DoneAnimatingEvent => e.type === "DONE_ANIMATING"),
        filter((e) => e.id === id)
      ),
    [id]
  );

  useSubscription(animationEnd$, (e) => {
    setAnimating(false);
  });

  const bind = useDrag(
    (e) => {
      const {
        down,
        xy: [x, y],
      } = e;
      if (down && !dragging) {
        //@ts-ignore
        startingPosition.current = ref.current!?.getBoundingClientRect();
        drag$.next({
          type: "DRAG_START",
          position: startingPosition.current,
          id,
        });
        setDragging(true);
      }
      if (!down && dragging) {
        setAnimating(true);
        setDragging(false);
        drag$.next({
          type: "DRAG_END",
          id,
        });
      }
      if (down) {
        drag$.next({
          type: "DRAGGING",
          gevent: e as FullGestureState<"drag">,
          id: id,
          startingPosition: startingPosition.current!,
        });
      }
    },
    { filterTaps: true }
  );

  if (dragging || (animating && !hasNoDestination)) {
    return (
      <Portaled>
        <DraggedNote
          starting={startingPosition.current!}
          noteId={id}
        ></DraggedNote>
      </Portaled>
    );
  }

  return (
    <>
      {animating && (
        <Portaled>
          <DraggedNote
            starting={startingPosition.current!}
            noteId={id}
          ></DraggedNote>
        </Portaled>
      )}
      <div
        ref={ref}
        className={
          animating && hasNoDestination
            ? css`
                opacity: 0;
              `
            : css`
                opacity: 1;
              `
        }
        style={{ height: controlledHeight ? "100%" : "initial" }}
      >
        <InnerNote id={id} bind={bind} controlledHeight={controlledHeight} />
      </div>
    </>
  );
});
